import type { SuspectedDuplicatePage, SuspectedDuplicatePair, SuspicionReason } from "../../../utils/nurseMergeModels";

/** The backend caps a page at 50 pairs; each pair is a tall row, so 25 is what fits a laptop screen twice. */
export const DUPLICATE_PAGE_SIZE_OPTIONS = [25, 50] as const;
export const DUPLICATE_PAGE_SIZE = DUPLICATE_PAGE_SIZE_OPTIONS[0];

/**
 * `match` reasons are evidence on their own (a phone, an email, a name); `supporting` ones only strengthen a pair
 * the backend already suggested, so they must never read as the headline.
 */
export type ReasonStrength = "match" | "supporting";

/** The record field a reason is evidence about — that field is lit on both records so the match is visible in place. */
export type MatchedField = "name" | "phone" | "email";

/** A merge-pair grid column: pinned in place, because the row order is the content (strongest evidence, or upload order). */
export const STATIC_PAIR_COLUMN = { sortable: false, disableColumnMenu: true, hideHeaderSeparator: true, resizable: false } as const;

const REASON_STRENGTH: Record<SuspicionReason, ReasonStrength> = {
	same_phone: "match",
	same_email: "match",
	same_name: "match",
	similar_name: "match",
	one_inactive: "supporting",
	bedside_and_admin: "supporting",
};

// Only exact matches are lit: a "similar" name highlighted on both sides would claim the two are identical.
const REASON_FIELD: Partial<Record<SuspicionReason, MatchedField>> = {
	same_phone: "phone",
	same_email: "email",
	same_name: "name",
};

export type ReasonChip = {
	code: SuspicionReason;
	labelKey: string;
	hintKey: string;
	strength: ReasonStrength;
};

/**
 * The chips for a pair's reasons: the backend's order (strongest first) within each strength, match evidence
 * always ahead of supporting evidence, each reason once.
 */
export const describeReasons = (reasons: readonly SuspicionReason[]): ReasonChip[] => {
	const unique = [...new Set(reasons)];
	const chips = unique.map((code) => ({
		code,
		labelKey: `nurse.merge.reason.${code}`,
		hintKey: `nurse.merge.reason.${code}.hint`,
		strength: REASON_STRENGTH[code],
	}));
	return [...chips.filter((chip) => chip.strength === "match"), ...chips.filter((chip) => chip.strength === "supporting")];
};

export const matchedFields = (reasons: readonly SuspicionReason[]): ReadonlySet<MatchedField> =>
	new Set(reasons.flatMap((reason) => (REASON_FIELD[reason] ? [REASON_FIELD[reason]] : [])));

export const pairRowId = (pair: SuspectedDuplicatePair): string => `${pair.first.nurseId}-${pair.second.nurseId}`;

const isPair = (pair: SuspectedDuplicatePair, firstNurseId: number, secondNurseId: number): boolean => {
	const ids = new Set([pair.first.nurseId, pair.second.nurseId]);
	return firstNurseId !== secondNurseId && ids.has(firstNurseId) && ids.has(secondNurseId);
};

/** A cached page without one pair (in either order), so a dismissed row leaves at once rather than after the refetch. */
export const withoutPair = (page: SuspectedDuplicatePage, firstNurseId: number, secondNurseId: number): SuspectedDuplicatePage => {
	const items = page.items.filter((pair) => !isPair(pair, firstNurseId, secondNurseId));
	if (items.length === page.items.length) return page;
	return { ...page, items, total: Math.max(0, page.total - 1) };
};

/** The last page that still has rows — where to land when removals emptied the page on screen. */
export const lastPageIndex = (total: number, pageSize: number): number => (total <= 0 ? 0 : Math.ceil(total / pageSize) - 1);
